import NumberFlow from '@number-flow/react';
import moment from 'moment';
import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { scoreTranslation } from '@treams/treams-analytics/src/score/services/ScoreConfig';
import { Card, Delta, FlexGap, Heading3, LineChart, Paragraph, tokens } from '@treams/treams-ui';

import type { TreamsCategoryScore } from '../TreamsScore.types';
import { formatDisplayScore, toDisplayPreviousScore, toDisplayScore } from '../scoreDisplay';
import type { ScoreSeriesPoint } from '../scoreSeries';
import { TreamsScoreHowCalculated } from './TreamsScoreHowCalculated';

interface IProps {
	total: TreamsCategoryScore;
	series: ScoreSeriesPoint[];
	periodStart: Date;
	showDelta: boolean;
	children?: ReactNode;
}

export function TreamsScoreCard({ total, series, periodStart, showDelta, children }: IProps) {
	const { t, i18n } = useTranslation();

	const displayTotal = toDisplayScore(total.score);
	const formatDate = (date: Date) => {
		const value = moment(date);
		if (i18n.language) value.locale(i18n.language);
		return value.format('D MMMM YYYY');
	};

	return (
		<Card>
			<Box minWidth={0}>
				<Heading3 mb={2}>{t('analytics.treamsScore.card.heading')}</Heading3>
				<FlexGap flexDirection="column" alignItems="flex-start" gap={tokens.gap.inline}>
					<Paragraph>{t('analytics.treamsScore.card.description')}</Paragraph>
					<TreamsScoreHowCalculated />
				</FlexGap>
			</Box>

			<Flex
				mt={4}
				flexDirection={['column', 'column', 'row']}
				alignItems={['stretch', 'stretch', 'center']}
				style={{ gap: 24 }}
			>
				<Flex
					flexDirection="column"
					alignItems="center"
					justifyContent="center"
					width={['100%', '100%', '300px']}
					style={{ flexShrink: 0, gap: 0 }}
				>
					<Box
						style={
							{
								fontFamily: tokens.typography.displayFamily,
								fontSize: 96,
								fontWeight: 900,
								lineHeight: 0.72,
								color: tokens.text.primary,
								// Monument Narrow and the digit mask leave empty space under the glyphs.
								'--number-flow-mask-height': '0px',
							} as CSSProperties
						}
					>
						<NumberFlow
							value={displayTotal}
							format={{ maximumFractionDigits: 0 }}
							locales="en-US"
						/>
					</Box>
					<Box
						style={{
							marginTop: tokens.gap.group,
							marginBottom: tokens.padding.section,
							fontSize: 18,
							fontWeight: 500,
							lineHeight: 1,
							color: tokens.text.primary,
						}}
					>
						{t(scoreTranslation(displayTotal))}
					</Box>
					{showDelta && (
						<Delta
							currentValue={displayTotal}
							previousValue={toDisplayPreviousScore(total.score, total.delta)}
							unit="numeric"
							animate
							label={t('analytics.treamsScore.card.comparedTo', {
								date: formatDate(periodStart),
							})}
						/>
					)}
				</Flex>

				<Box flexGrow={1} minWidth={0}>
					<LineChart
						labels={series.map((point) => point.label)}
						data={series.map((point) => point.value)}
						bounds={[0, 100]}
						height={220}
						tintColor={tokens.brand.icon}
						highlightLastPoint
						bleed={false}
						yFormat={(value) => formatDisplayScore(value)}
					/>
				</Box>
			</Flex>

			{children}
		</Card>
	);
}
